// JSON-LD Schema generators for GEO (Generative Engine Optimization)

export function getOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Annotate.tv',
    url: 'https://annotate.tv',
    logo: 'https://annotate.tv/img/logos/apple-touch-icon.png',
    description:
      'Annotate.tv is a video annotation tool for YouTube that helps users take timestamped notes, chat with videos using AI, extract text from slides, and export notes to popular apps.',
    sameAs: ['https://twitter.com/Annotate_TV'],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'mati@annotate.tv',
      contactType: 'customer support',
    },
  }
}

export function getWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Annotate.tv',
    url: 'https://annotate.tv',
    description:
      'Annotate.tv is a video annotation tool for YouTube. Take timestamped notes, chat with videos using AI, extract text from slides, and export to Notion, Evernote, and Readwise.',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://annotate.tv/watch/{video_id}',
      },
      'query-input': 'required name=video_id',
    },
  }
}

export function getSoftwareApplicationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Annotate.tv',
    applicationCategory: 'ProductivityApplication',
    operatingSystem: 'Web browser',
    url: 'https://annotate.tv',
    description:
      'Annotate.tv is a web-based video annotation tool that lets you take timestamped notes on YouTube videos, chat with videos using AI, extract text from slides using OCR, and export notes to PDF, Notion, Evernote, Obsidian, and Readwise.',
    offers: [
      {
        '@type': 'Offer',
        name: 'Free Starter',
        price: '0',
        priceCurrency: 'USD',
        description:
          'Free plan with up to 5 videos, unlimited notes, playlists, and public sharing',
      },
      {
        '@type': 'Offer',
        name: 'Premium Monthly',
        price: '5',
        priceCurrency: 'USD',
        billingIncrement: 'P1M',
        description:
          'Unlimited videos, transcript access, AI chat with video, text extraction from slides, PDF export, Markdown export, and Readwise sync',
      },
      {
        '@type': 'Offer',
        name: 'Premium Yearly',
        price: '48',
        priceCurrency: 'USD',
        billingIncrement: 'P1Y',
        description: 'All Premium features at $4/month (20% savings)',
      },
    ],
    featureList: [
      'Timestamped video notes',
      'YouTube and Vimeo support',
      'AI chat with video transcript',
      'Text extraction from video slides (OCR)',
      'Video transcript viewing',
      'PDF export',
      'Markdown export for Notion, Evernote, Obsidian',
      'Readwise sync',
      'Search across all notes',
      'Playlist organization',
      'Public note sharing',
    ],
    screenshot: 'https://annotate.tv/img/og-image.png',
  }
}

export function getFAQPageSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  }
}

export function getHowToSchema({ name, description, steps }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
      ...(step.image && { image: step.image }),
    })),
  }
}

export function getBreadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `https://annotate.tv${item.path}`,
    })),
  }
}

export function getVideoObjectSchema({ video, annotations = [] }) {
  const thumbnailUrl =
    video.thumbnails?.maxres?.url || video.thumbnails?.default?.url

  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: video.title,
    description: video.description?.slice(0, 200) || video.title,
    thumbnailUrl,
    uploadDate: video.createdAt || new Date().toISOString(),
    duration: video.duration
      ? `PT${Math.floor(video.duration / 60)}M${video.duration % 60}S`
      : undefined,
    embedUrl: `https://www.youtube.com/embed/${getYouTubeIdFromUrl(video.url)}`,
    contentUrl: video.url,
    ...(annotations.length > 0 && {
      hasPart: annotations.slice(0, 10).map((annotation) => ({
        '@type': 'Clip',
        name: annotation.title || 'Note',
        startOffset: annotation.startTime || 0,
        description: annotation.text?.slice(0, 100),
      })),
    }),
  }
}

function getYouTubeIdFromUrl(url) {
  if (!url) return ''
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/)
  return match ? match[1] : ''
}
